import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Badge, 
  Alert,
  Spinner,
  Tabs,
  Tab
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './UserManagement.css'; // Reuse styles

const SignatoryManagement = () => {
  const { token, user } = useAuth();
  const [signatories, setSignatories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSignatory, setEditingSignatory] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [formErrors, setFormErrors] = useState({});
  const [moduleFilter, setModuleFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formData, setFormData] = useState({
    module_type: 'evaluationReport',
    signatory_type: '',
    user_id: '',
    name: '',
    honorific: '',
    designation: '',
    signature_image: null,
    signature_image_preview: null
  });

  // Honorific options
  const honorificOptions = [
    { value: '', label: 'None' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Engr.', label: 'Engr.' },
    { value: 'Prof.', label: 'Prof.' },
    { value: 'Asst. Prof.', label: 'Asst. Prof.' },
    { value: 'Assoc. Prof.', label: 'Assoc. Prof.' },
    { value: 'Atty.', label: 'Atty.' },
    { value: 'Rev.', label: 'Rev.' }
  ];

  const isAdmin = user?.role === 'Admin';

  // Module types and their signatory types
  const moduleTypes = {
    evaluationReport: {
      label: 'Evaluation Report',
      signatoryTypes: [
        { value: 'reviewedBy', label: 'Reviewed By' },
        { value: 'acceptedBy', label: 'Accepted By' }
      ]
    },
    monitoringTool: {
      label: 'Monitoring Tool',
      signatoryTypes: [
        { value: 'monitoredBy', label: 'Monitored By' },
        { value: 'reviewedBy', label: 'Reviewed & Evaluated By' }
      ]
    },
    projectProposal: {
      label: 'Project Proposal',
      signatoryTypes: [
        { value: 'reviewedBy', label: 'Reviewed By' },
        { value: 'recommendingApproval1', label: 'Recommending Approval 1' },
        { value: 'recommendingApproval2', label: 'Recommending Approval 2' },
        { value: 'approvedBy', label: 'Approved By' }
      ]
    },
    projectProposalNoLIB: {
      label: 'Project Proposal with no LIB',
      signatoryTypes: [
        { value: 'reviewedBy', label: 'Reviewed By' },
        { value: 'recommendingApproval1', label: 'Recommending Approval 1' },
        { value: 'approvedBy', label: 'Approved By' }
      ]
    },
    lineItemBudget: {
      label: 'Line Item Budget',
      signatoryTypes: [
        { value: 'checkedBy', label: 'Checked and Verified By' },
        { value: 'certifiedBy', label: 'Certified Funds Available' },
        { value: 'recommendingApproval1', label: 'Recommending Approval 1' },
        { value: 'recommendingApproval2', label: 'Recommending Approval 2' },
        { value: 'approvedBy', label: 'Approved By' }
      ]
    },
    workPlan: {
      label: 'Work Plan',
      signatoryTypes: [
        { value: 'checkedAndVerified', label: 'Checked and Verified By' },
        { value: 'approvedBy', label: 'Approved By' }
      ]
    }
  };

  // Set up axios default headers
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.timeout = 30000;
    }
  }, [token]);

  // Redirect if not Admin
  useEffect(() => {
    if (user && !isAdmin) {
      window.location.href = '/dashboard';
    }
  }, [user, isAdmin]);

  // Fetch signatories
  const fetchSignatories = async () => {
    try {
      setLoading(true);
      const url = moduleFilter 
        ? `/signatories?module_type=${moduleFilter}`
        : '/signatories';
      const response = await axios.get(url);
      if (response.data.success) {
        setSignatories(response.data.signatories || []);
      }
    } catch (error) {
      console.error('Error fetching signatories:', error);
      setAlert({
        show: true,
        message: 'Failed to load signatories: ' + (error.response?.data?.error || error.message),
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchSignatories();
      fetchUsers();
    }
  }, [moduleFilter, isAdmin]);

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch signature image from user's signature_path
  const fetchUserSignature = async (signaturePath) => {
    if (!signaturePath) return null;
    
    try {
      let imageUrl = signaturePath;
      
      // If it's a relative path, construct the full URL
      if (!signaturePath.startsWith('http')) {
        const baseURL = process.env.REACT_APP_API_URL 
          ? process.env.REACT_APP_API_URL.replace('/api', '')
          : 'http://localhost:5000';
        imageUrl = signaturePath.startsWith('/') 
          ? `${baseURL}${signaturePath}`
          : `${baseURL}/${signaturePath}`;
      }
      
      const response = await axios.get(imageUrl, { 
        responseType: 'blob',
        timeout: 10000
      });
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read signature image'));
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.error('Error fetching signature image:', error);
      return null;
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If user is selected, auto-populate name, designation, and fetch signature
    if (name === 'user_id' && value) {
      const selectedUser = users.find(u => u.user_id === parseInt(value));
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          user_id: value,
          name: selectedUser.fullname || '',
          designation: selectedUser.role || ''
        }));
        
        // Fetch signature image
        if (selectedUser.signature_path) {
          fetchUserSignature(selectedUser.signature_path).then(signatureData => {
            if (signatureData) {
              setFormData(prev => ({
                ...prev,
                signature_image: signatureData,
                signature_image_preview: signatureData
              }));
            }
          });
        } else {
          setFormData(prev => ({
            ...prev,
            signature_image: null,
            signature_image_preview: null
          }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Allow admin to manually upload/override the signature image for this signatory
  const handleSignatureFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setFormData(prev => ({
        ...prev,
        signature_image: dataUrl,
        signature_image_preview: dataUrl
      }));
    };
    reader.readAsDataURL(file);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      module_type: 'evaluationReport',
      signatory_type: '',
      user_id: '',
      name: '',
      honorific: '',
      designation: '',
      signature_image: null,
      signature_image_preview: null
    });
    setEditingSignatory(null);
    setFormErrors({});
  };

  // Open modal for editing
  const handleEdit = (signatory) => {
    setEditingSignatory(signatory);
    
    // Try to find matching user by name (handle names with or without honorifics)
    const signatoryName = signatory.name || '';
    const matchingUser = users.find(u => {
      const userName = u.fullname || '';
      // Check if names match (with or without honorific)
      return userName === signatoryName || 
             userName === signatoryName.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Engr\.|Prof\.|Asst\. Prof\.|Assoc\. Prof\.|Atty\.|Rev\.)\s+/i, '').trim() ||
             signatoryName === userName.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Engr\.|Prof\.|Asst\. Prof\.|Assoc\. Prof\.|Atty\.|Rev\.)\s+/i, '').trim();
    });
    
    // Extract honorific - prioritize database honorific over name-based extraction
    let honorific = signatory.honorific || '';
    let nameWithoutHonorific = signatoryName;
    
    // If honorific is in the database, use it and extract name
    if (honorific) {
      // Remove honorific from name if it's already there
      const honorificPattern = new RegExp(`^${honorific.replace('.', '\\.')}\\s+`, 'i');
      nameWithoutHonorific = signatoryName.replace(honorificPattern, '').trim();
    } else {
      // If no honorific in DB, try to extract from name
      const honorificMatch = signatoryName.match(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Engr\.|Prof\.|Asst\. Prof\.|Assoc\. Prof\.|Atty\.|Rev\.)\s+(.+)/i);
      if (honorificMatch) {
        honorific = honorificMatch[1];
        nameWithoutHonorific = honorificMatch[2];
      }
    }
    
    setFormData({
      module_type: signatory.module_type,
      signatory_type: signatory.signatory_type,
      user_id: matchingUser ? matchingUser.user_id.toString() : '',
      name: nameWithoutHonorific,
      honorific: honorific,
      designation: signatory.designation,
      signature_image: signatory.signature_image ? `data:image/png;base64,${signatory.signature_image}` : null,
      signature_image_preview: signatory.signature_image ? `data:image/png;base64,${signatory.signature_image}` : null
    });
    setShowModal(true);
  };

  // Open modal for creating new
  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    // Validation
    const errors = {};
    if (!formData.module_type) errors.module_type = 'Module type is required';
    if (!formData.signatory_type) errors.signatory_type = 'Signatory type is required';
    if (!formData.user_id) errors.user_id = 'Please select a user';
    if (!formData.designation || formData.designation.trim() === '') errors.designation = 'Designation is required';
    
    // Ensure name is set from selected user
    if (formData.user_id) {
      const selectedUser = users.find(u => u.user_id === parseInt(formData.user_id));
      if (selectedUser && !formData.name) {
        formData.name = selectedUser.fullname || '';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      // Prepare data for API
      const submitData = {
        module_type: formData.module_type,
        signatory_type: formData.signatory_type,
        name: formData.name.trim(),
        honorific: formData.honorific || null,
        designation: formData.designation.trim()
      };

      // Add signature image if provided (remove data:image prefix)
      if (formData.signature_image) {
        submitData.signature_image = formData.signature_image.split(',')[1];
      }

      if (editingSignatory) {
        // Update existing signatory
        await axios.put(`/signatories/${editingSignatory.signatory_id}`, submitData);
        setAlert({
          show: true,
          message: 'Signatory updated successfully!',
          variant: 'success'
        });
      } else {
        // Create new signatory
        await axios.post('/signatories', submitData);
        setAlert({
          show: true,
          message: 'Signatory created successfully!',
          variant: 'success'
        });
      }

      setShowModal(false);
      resetForm();
      fetchSignatories();
    } catch (error) {
      console.error('Error saving signatory:', error);
      setAlert({
        show: true,
        message: error.response?.data?.error || 'Failed to save signatory',
        variant: 'danger'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (signatory) => {
    if (!window.confirm(`Are you sure you want to delete the signatory "${signatory.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/signatories/${signatory.signatory_id}`);
      setAlert({
        show: true,
        message: 'Signatory deleted successfully!',
        variant: 'success'
      });
      fetchSignatories();
    } catch (error) {
      console.error('Error deleting signatory:', error);
      setAlert({
        show: true,
        message: error.response?.data?.error || 'Failed to delete signatory',
        variant: 'danger'
      });
    }
  };

  // Filter signatories by module
  const filteredSignatories = moduleFilter
    ? signatories.filter(s => s.module_type === moduleFilter)
    : signatories;

  // Group signatories by module
  const groupedSignatories = filteredSignatories.reduce((acc, sig) => {
    if (!acc[sig.module_type]) {
      acc[sig.module_type] = [];
    }
    acc[sig.module_type].push(sig);
    return acc;
  }, {});

  if (!isAdmin) {
    return null;
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Signatory Management</h4>
              <Button variant="primary" onClick={handleCreate}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Signatory
              </Button>
            </Card.Header>
            <Card.Body>
              {alert.show && (
                <Alert 
                  variant={alert.variant} 
                  dismissible 
                  onClose={() => setAlert({ show: false, message: '', variant: 'success' })}
                >
                  {alert.message}
                </Alert>
              )}

              {/* Module Filter */}
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter by Module</Form.Label>
                    <Form.Select
                      value={moduleFilter}
                      onChange={(e) => setModuleFilter(e.target.value)}
                    >
                      <option value="">All Modules</option>
                      {Object.keys(moduleTypes).map(key => (
                        <option key={key} value={key}>{moduleTypes[key].label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading signatories...</p>
                </div>
              ) : filteredSignatories.length === 0 ? (
                <Alert variant="info">
                  No signatories found. Click "Add Signatory" to create one.
                </Alert>
              ) : (
                <Tabs defaultActiveKey={Object.keys(groupedSignatories)[0] || 'all'} className="mb-3">
                  {Object.keys(groupedSignatories).map(moduleType => (
                    <Tab key={moduleType} eventKey={moduleType} title={moduleTypes[moduleType]?.label || moduleType}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Signatory Type</th>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedSignatories[moduleType].map(signatory => (
                            <tr key={signatory.signatory_id}>
                              <td>
                                {moduleTypes[signatory.module_type]?.signatoryTypes
                                  .find(st => st.value === signatory.signatory_type)?.label || signatory.signatory_type}
                              </td>
                              <td>
                                {signatory.honorific ? `${signatory.honorific} ${signatory.name}` : signatory.name}
                              </td>
                              <td>{signatory.designation}</td>
                              <td>
                                <Badge bg={signatory.is_active ? 'success' : 'secondary'}>
                                  {signatory.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEdit(signatory)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(signatory)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Tab>
                  ))}
                </Tabs>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingSignatory ? 'Edit Signatory' : 'Add New Signatory'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Module Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="module_type"
                    value={formData.module_type}
                    onChange={handleChange}
                    isInvalid={!!formErrors.module_type}
                    disabled={!!editingSignatory}
                  >
                    {Object.keys(moduleTypes).map(key => (
                      <option key={key} value={key}>{moduleTypes[key].label}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.module_type}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Signatory Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="signatory_type"
                    value={formData.signatory_type}
                    onChange={handleChange}
                    isInvalid={!!formErrors.signatory_type}
                    disabled={!!editingSignatory}
                  >
                    <option value="">Select signatory type</option>
                    {moduleTypes[formData.module_type]?.signatoryTypes.map(st => {
                      // Check if this signatory type already exists for this module
                      const existingSignatory = signatories.find(
                        s => s.module_type === formData.module_type && 
                             s.signatory_type === st.value && 
                             s.is_active &&
                             (!editingSignatory || s.signatory_id !== editingSignatory.signatory_id)
                      );
                      const isDisabled = !!existingSignatory;
                      
                      return (
                        <option 
                          key={st.value} 
                          value={st.value}
                          disabled={isDisabled}
                        >
                          {st.label} {isDisabled ? '(Already exists)' : ''}
                        </option>
                      );
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.signatory_type}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select User <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleChange}
                    isInvalid={!!formErrors.user_id}
                    disabled={loadingUsers}
                  >
                    <option value="">Select a user...</option>
                    {users.map(u => {
                      // Check if this user is already assigned to an existing signatory for this module and signatory type
                      const existingSignatory = signatories.find(
                        s => s.module_type === formData.module_type && 
                             s.signatory_type === formData.signatory_type && 
                             s.name === u.fullname &&
                             s.is_active &&
                             (!editingSignatory || s.signatory_id !== editingSignatory.signatory_id)
                      );
                      const isDisabled = !!existingSignatory;
                      
                      return (
                        <option 
                          key={u.user_id} 
                          value={u.user_id}
                          disabled={isDisabled}
                        >
                          {u.fullname} {u.role ? `(${u.role})` : ''} {isDisabled ? '- Already assigned' : ''}
                        </option>
                      );
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.user_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Honorific</Form.Label>
                  <Form.Select
                    name="honorific"
                    value={formData.honorific}
                    onChange={handleChange}
                  >
                    {honorificOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    isInvalid={!!formErrors.designation}
                    placeholder="Enter job title/position"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.designation}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Signature (optional – overrides user account signature)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={handleSignatureFileChange}
                  />
                  {formData.signature_image_preview && (
                    <div className="mt-2">
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Current signature preview:
                      </div>
                      <img
                        src={formData.signature_image_preview}
                        alt="Signature preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 120,
                          border: '1px solid #ddd',
                          padding: 4,
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                editingSignatory ? 'Update' : 'Add'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SignatoryManagement;
